// MapLibre source ids and layer specs. Six GeoJSON sources, updated in place
// via setData; sources and layers are re-added on every style.load (the
// basemap style is swapped when the theme changes, which wipes both). The
// point-pin sources (places, routes, outings, events) cluster at low zoom —
// see CLUSTER_SOURCE_OPTIONS.
import type { AddLayerObject, FilterSpecification } from "maplibre-gl";
import type { ResolvedTheme } from "../lib/theme";
import { PLACE_LABELS_MIN_ZOOM } from "./zoomConfig";

export const SOURCES = {
  places: "gaea-places",
  routes: "gaea-routes",
  routeLines: "gaea-route-lines",
  outings: "gaea-outings",
  regions: "gaea-regions",
  events: "gaea-events",
} as const;

/**
 * Clustering is a *source* option, so it must be set when MapView registers
 * the sources on style.load — setData afterwards is unaffected. Every point
 * pin source clusters; route lines are geometry and regions are polygons, so
 * neither can. Routes only fetch from ROUTES_MIN_ZOOM, so their clusters only
 * ever appear in the ROUTES_MIN_ZOOM..clusterMaxZoom band — still worth it in
 * dense areas.
 *
 * clusterMaxZoom 10: pins fully decluster by zoom 11, where the tight feature
 * budgets kick in (TRACKS_MIN_ZOOM) and individual pins are readable.
 */
export const CLUSTER_SOURCE_OPTIONS: Partial<Record<string, { cluster: true; clusterMaxZoom: number; clusterRadius: number }>> = {
  [SOURCES.places]: { cluster: true, clusterMaxZoom: 10, clusterRadius: 50 },
  [SOURCES.routes]: { cluster: true, clusterMaxZoom: 10, clusterRadius: 50 },
  [SOURCES.outings]: { cluster: true, clusterMaxZoom: 10, clusterRadius: 50 },
  [SOURCES.events]: { cluster: true, clusterMaxZoom: 10, clusterRadius: 50 },
};

// Routes carry the app's primary blue; the rest are spaced around the wheel so
// overlapping pin types stay distinguishable at small radii.
export const COLORS = {
  place: "#0d9488",
  route: "#2563eb",
  outing: "#f97316",
  region: "#8b5cf6",
  // Rose sits between outing orange and region violet on the wheel and stays
  // readable on both basemaps.
  event: "#db2777",
};

export const INTERACTIVE_LAYERS = ["place-pins", "route-pins", "outing-pins", "event-pins", "route-lines"] as const;

// Draw-a-route mode (F43): the in-progress line and its vertices live in two
// extra sources on the main map, empty unless MapView's `draw` prop is set.
export const DRAW_SOURCES = {
  line: "gaea-draw-line",
  vertices: "gaea-draw-vertices",
} as const;

export const DRAW_VERTEX_LAYER = "draw-vertices";

/** Draw layers go last in layerSpecs so the in-progress route sits on top. */
export function drawLayerSpecs(): AddLayerObject[] {
  return [
    {
      id: "draw-line",
      type: "line",
      source: DRAW_SOURCES.line,
      layout: { "line-cap": "round", "line-join": "round" },
      paint: { "line-color": COLORS.route, "line-width": 3, "line-opacity": 0.9 },
    },
    {
      id: DRAW_VERTEX_LAYER,
      type: "circle",
      source: DRAW_SOURCES.vertices,
      paint: {
        "circle-radius": ["case", ["get", "selected"], 8, 6],
        "circle-color": ["case", ["get", "selected"], "#dc2626", COLORS.route],
        "circle-stroke-width": 2,
        "circle-stroke-color": "#ffffff",
      },
    },
  ];
}

/**
 * Cluster layers get their own interaction wiring (click zooms into the
 * cluster instead of navigating — cluster features carry cluster_id, not
 * kind/id). Count labels are listed too so a click landing on the number
 * still expands.
 */
export const CLUSTER_LAYERS: { id: string; source: string }[] = [
  { id: "place-clusters", source: SOURCES.places },
  { id: "place-cluster-counts", source: SOURCES.places },
  { id: "route-clusters", source: SOURCES.routes },
  { id: "route-cluster-counts", source: SOURCES.routes },
  { id: "outing-clusters", source: SOURCES.outings },
  { id: "outing-cluster-counts", source: SOURCES.outings },
  { id: "event-clusters", source: SOURCES.events },
  { id: "event-cluster-counts", source: SOURCES.events },
];

// Clustered sources emit synthetic cluster features alongside the leftover
// singles; the original pin/label layers must skip the synthetic ones.
const UNCLUSTERED: FilterSpecification = ["!", ["has", "point_count"]];
const CLUSTERED: FilterSpecification = ["has", "point_count"];

/**
 * Layer specs for the current theme. Label halos flip from white to a dark
 * slate so text stays readable on the dark basemap; the pin/line colors work
 * on both.
 */
export function layerSpecs(theme: ResolvedTheme): AddLayerObject[] {
  const halo = theme === "dark" ? "#0f172a" : "#ffffff";
  return [
  {
    id: "region-fills",
    type: "fill",
    source: SOURCES.regions,
    paint: { "fill-color": COLORS.region, "fill-opacity": 0.08 },
  },
  {
    id: "region-outlines",
    type: "line",
    source: SOURCES.regions,
    paint: { "line-color": COLORS.region, "line-width": 1.5, "line-dasharray": [2, 2], "line-opacity": 0.6 },
  },
  {
    id: "region-labels",
    type: "symbol",
    source: SOURCES.regions,
    layout: {
      "text-field": ["get", "name"],
      "text-size": 13,
      "text-font": ["Noto Sans Bold"],
    },
    paint: { "text-color": COLORS.region, "text-halo-color": halo, "text-halo-width": 1.5 },
  },
  {
    id: "route-lines",
    type: "line",
    source: SOURCES.routeLines,
    layout: { "line-cap": "round", "line-join": "round" },
    paint: {
      "line-color": COLORS.route,
      "line-width": ["case", ["boolean", ["feature-state", "hover"], false], 4, 2.5],
      "line-opacity": 0.75,
    },
  },
  {
    id: "route-pins",
    type: "circle",
    source: SOURCES.routes,
    filter: UNCLUSTERED,
    paint: {
      "circle-color": COLORS.route,
      "circle-radius": ["interpolate", ["linear"], ["zoom"], 8, 3.5, 13, 6],
      "circle-stroke-width": 1.5,
      "circle-stroke-color": "#ffffff",
      "circle-opacity": 0.9,
    },
  },
  {
    id: "outing-pins",
    type: "circle",
    source: SOURCES.outings,
    filter: UNCLUSTERED,
    paint: {
      // Planned outings (owner-only via RLS, so only ever the viewer's own)
      // render hollow — fill matches the basemap, outline keeps the outing
      // orange — so "not done yet" reads at a glance next to solid pins.
      "circle-color": ["case", ["==", ["get", "status"], "PLANNED"], halo, COLORS.outing],
      "circle-radius": ["interpolate", ["linear"], ["zoom"], 6, 5, 13, 8],
      "circle-stroke-width": 2,
      "circle-stroke-color": ["case", ["==", ["get", "status"], "PLANNED"], COLORS.outing, "#ffffff"],
    },
  },
  {
    id: "event-pins",
    type: "circle",
    source: SOURCES.events,
    filter: UNCLUSTERED,
    paint: {
      "circle-color": COLORS.event,
      "circle-radius": ["interpolate", ["linear"], ["zoom"], 6, 5, 13, 8],
      "circle-stroke-width": 2,
      "circle-stroke-color": "#ffffff",
    },
  },
  {
    id: "event-labels",
    type: "symbol",
    source: SOURCES.events,
    filter: UNCLUSTERED,
    minzoom: PLACE_LABELS_MIN_ZOOM,
    layout: {
      "text-field": ["get", "name"],
      "text-size": 11,
      "text-font": ["Noto Sans Regular"],
      "text-offset": [0, 1.1],
      "text-anchor": "top",
      "text-optional": true,
    },
    paint: { "text-color": COLORS.event, "text-halo-color": halo, "text-halo-width": 1.2 },
  },
  {
    id: "place-pins",
    type: "circle",
    source: SOURCES.places,
    filter: UNCLUSTERED,
    paint: {
      "circle-color": COLORS.place,
      "circle-radius": ["interpolate", ["linear"], ["zoom"], 6, 4.5, 13, 7],
      "circle-stroke-width": 2,
      "circle-stroke-color": "#ffffff",
    },
  },
  {
    id: "place-labels",
    type: "symbol",
    source: SOURCES.places,
    filter: UNCLUSTERED,
    minzoom: PLACE_LABELS_MIN_ZOOM,
    layout: {
      "text-field": ["get", "name"],
      "text-size": 11,
      "text-font": ["Noto Sans Regular"],
      "text-offset": [0, 1.1],
      "text-anchor": "top",
      "text-optional": true,
    },
    paint: { "text-color": COLORS.place, "text-halo-color": halo, "text-halo-width": 1.2 },
  },
  // Cluster bubbles sit above the pin layers. Each keeps its kind's hue —
  // deepening as the count steps up — so a teal blob still reads "places" and
  // an orange one "outings" even when fully aggregated. The stroke uses the
  // theme halo, matching how every other layer adapts to the dark basemap.
  clusterCircles("place-clusters", SOURCES.places, ["#2dd4bf", "#0d9488", "#0f766e"], halo),
  clusterCounts("place-cluster-counts", SOURCES.places),
  clusterCircles("route-clusters", SOURCES.routes, ["#60a5fa", "#2563eb", "#1d4ed8"], halo),
  clusterCounts("route-cluster-counts", SOURCES.routes),
  clusterCircles("outing-clusters", SOURCES.outings, ["#fb923c", "#f97316", "#c2410c"], halo),
  clusterCounts("outing-cluster-counts", SOURCES.outings),
  clusterCircles("event-clusters", SOURCES.events, ["#f472b6", "#db2777", "#9d174d"], halo),
  clusterCounts("event-cluster-counts", SOURCES.events),
  ];
}

// Step breakpoints (25/100) suit the map's feature budgets: a low-zoom
// viewport tops out in the low thousands per layer, so these split
// small/medium/large clusters usefully.
function clusterCircles(id: string, source: string, [small, medium, large]: [string, string, string], halo: string): AddLayerObject {
  return {
    id,
    type: "circle",
    source,
    filter: CLUSTERED,
    paint: {
      "circle-color": ["step", ["get", "point_count"], small, 25, medium, 100, large],
      "circle-radius": ["step", ["get", "point_count"], 13, 25, 17, 100, 22],
      "circle-stroke-width": 2,
      "circle-stroke-color": halo,
      "circle-opacity": 0.9,
    },
  };
}

function clusterCounts(id: string, source: string): AddLayerObject {
  return {
    id,
    type: "symbol",
    source,
    filter: CLUSTERED,
    layout: {
      "text-field": ["get", "point_count_abbreviated"],
      "text-size": 12,
      "text-font": ["Noto Sans Bold"],
      // Never drop the number under collision pressure; it IS the cluster.
      "text-allow-overlap": true,
    },
    // White on the saturated bubble is readable on both basemaps.
    paint: { "text-color": "#ffffff" },
  };
}
